/* v3.9 · 5 AM War Room — Command Center.
 *
 * Three-column live command view for the plant manager's morning stand-up.
 *   • LEFT   Jobs list for the selected day (mini agenda w/ status pulse)
 *   • MIDDLE Fleet Status — pre-start safety lockout (red flash · STOP block)
 *   • RIGHT  Live Google Map — real Traffic layer, smooth-glide markers,
 *            click a truck to see current load (MPa + Volume) and ETA
 *
 * Sticky top KPI strip shows Today's Volume + Today's Revenue.
 * Auto-refreshes: dashboard KPIs & jobs every 60 s, truck positions every 30 s.
 *
 * Reuses backend endpoints only (no new APIs required):
 *   GET /api/dashboard             · production_m3, revenue_today, jobs, trucks
 *   GET /api/trucks/tracks         · live positions + polyline route_to_site
 *   GET /api/trucks?plant_id=      · full fleet w/ capacity_m3
 *   GET /api/mixes                 · mix designs (strength_mpa)
 *   GET /api/admin/fleet-health    · pre-start snapshots for safety lockout
 *   GET /api/customers             · site name lookup
 */
import { useCallback, useEffect, useMemo, useRef, useState, Component } from "react";
import { GoogleMap, useJsApiLoader, TrafficLayer, OverlayView, Polyline } from "@react-google-maps/api";
import {
  Radar, Truck as TruckIcon, MapPin, Clock, AlertTriangle, Factory,
  Radio, Package, DollarSign, ShieldOff, ChevronRight, ChevronDown, Zap, X, Eye, HardHat, Smartphone, Check, Plus, Phone, Copy, Loader2, Pencil,
} from "lucide-react";
import { api, SA_ORG_KEY, WS_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isFleet } from "@/lib/industry";
import useLiveRefresh from "@/hooks/useLiveRefresh";
import { toast } from "sonner";
import { usePlant } from "@/context/PlantContext";
import { localToday, shiftLocalDate, formatLocalDate } from "@/lib/dates";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { announceReady, speakTruckBack, autoArmOnFirstGesture } from "@/lib/voice";
import PrintScheduleButton from "@/components/PrintScheduleButton";
import PriceCalcPanel from "@/components/PriceCalcPanel";
import WeatherReplyWatcher from "@/components/WeatherReplyWatcher";
import { QuickJobDrawer } from "@/components/QuickJobDrawer";
import LoaderGpsDialog from "@/components/LoaderGpsDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const GOOGLE_LIBRARIES = [];       // Stable reference — prevents LoadScript re-init warning.
const MAP_VIEW_KEY = "ab_war_room_map_view";  // standard | dark | satellite — per device

// Keeps a Google Maps runtime failure (e.g. RefererNotAllowedMapError on an un-allow-listed
// host) from tearing down the whole War Room — the Jobs/Fleet columns stay usable.
class MapErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.warn("War Room map failed to render:", err?.message || err); }
  render() {
    if (this.state.failed) {
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-industrial text-xs mono px-6 text-center" data-testid="war-room-map-error">
          <MapPin className="w-6 h-6 opacity-50" />
          Live map unavailable here — the Jobs &amp; Fleet panels are live.
        </div>
      );
    }
    return this.props.children;
  }
}

// v3.16 · Silver / Industrial map theme.  Hides shops, parks, transit and every
// other POI so operators only see roads, truck pins and job-site markers.
// Palette leans grey-on-grey with warm road highlights to complement the app's
// orange accents.
const WAR_ROOM_MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#1F2126" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8B8F97" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#141518" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#3A3F47" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#C6C8CC" }] },
  // Hide EVERY point-of-interest (shops, parks, schools, gyms, restaurants…).
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "poi.attraction", stylers: [{ visibility: "off" }] },
  { featureType: "poi.medical", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", stylers: [{ visibility: "off" }] },
  { featureType: "poi.school", stylers: [{ visibility: "off" }] },
  { featureType: "poi.place_of_worship", stylers: [{ visibility: "off" }] },
  { featureType: "poi.sports_complex", stylers: [{ visibility: "off" }] },
  // Transit stations off — trucks and job sites only.
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  // Roads: warm grey ramp, arterials pop, motorways in a subtle orange.
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#2A2D33" }] },
  { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#26292E" }] },
  { featureType: "road.local", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#353A42" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#4A3E2B" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#F57C00" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#D8B98B" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0F1215" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#5B6067" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#1A1C20" }] },
  { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#22252A" }] },
];
const CAMBRIDGE = { lat: -42.835973, lng: 147.450342 }; // v3.15e · exact GADTECH yard coords — fallback only, live plant record is loaded from /api/plants

// Pre-start "red flash" thresholds mirror Command.jsx / Dispatch flow so the
// war-room operator sees the same safety verdict.
const needsPreStartFlag = (ps) =>
  !!ps && (!ps.submitted || (ps.overall === "fail" && !ps.resolved));

const money = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 })
    .format(Number(n || 0));

const STATUS_TINT = {
  available:      "#22C55E",
  loading:        "#F97316",
  slumping:       "#FBBF24",
  en_route:       "#3B82F6",
  on_site:        "#A855F7",
  discharging:    "#A855F7",
  returning:      "#0EA5E9",
  out_of_service: "#71717A",
  maintenance:    "#71717A",
};

// Which statuses get a soft flashing row on the fleet board, and in what colour.
const FLASH_RGB = {
  available: "34, 197, 94",    // green
  en_route:  "59, 130, 246",   // blue
  on_site:   "168, 85, 247",   // purple
};
const DARK_RGB = "239, 68, 68";       // a truck gone dark — red
const DARK_AFTER_S = 180;             // matches the backend dark-truck watchdog
const ON_ROAD = new Set(["loading", "slumping", "en_route", "on_site", "discharging", "returning"]);
const RETURNING_SET = new Set(["returning", "on_site", "discharging", "en_route"]);
const kmDist = (a, b, c, d) => { const R = 6371, p = Math.PI / 180, x = (c - a) * p, y = (d - b) * p; const h = Math.sin(x / 2) ** 2 + Math.cos(a * p) * Math.cos(c * p) * Math.sin(y / 2) ** 2; return 2 * R * Math.asin(Math.sqrt(h)); };

// SVG-encoded pin icon so we can control colour + selection ring without shipping images.
function truckPinDataUrl(color = "#F97316", selected = false, heading = null) {
  const ring = selected
    ? `<circle cx="24" cy="24" r="22" fill="none" stroke="#FFFFFF" stroke-width="3"/>
       <circle cx="24" cy="24" r="22" fill="none" stroke="${color}" stroke-width="2" stroke-dasharray="4 3"/>`
    : "";
  const rot = Number.isFinite(Number(heading)) ? `rotate(${Number(heading)} 24 24)` : "";
  const arrow = Number.isFinite(Number(heading))
    ? `<path d="M24 3 L29 11 L19 11 Z" fill="${color}" stroke="#0A0A0B" stroke-width="1" transform="${rot}"/>`
    : "";
  // Agitator (mixer) truck silhouette — cab, drum, chute, wheels — tinted by status colour.
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
  ${ring}
  <circle cx="24" cy="24" r="16" fill="${color}" stroke="#0A0A0B" stroke-width="2"/>
  ${arrow}
  <g fill="#0A0A0B">
    <path d="M12 27h6v-7h3l2-3h7v10h4v-3l-2-3h-2v-3h-9l-2 3h-3z" opacity="0.15"/>
    <rect x="11" y="19" width="6" height="8" rx="1"/>
    <path d="M17 27h8l3-2 2-6h-7l-2 3h-4z"/>
    <path d="M28 19c3 0 5 2 5 4l-1 4h-5l1-8z"/>
    <circle cx="15" cy="29.5" r="2.4"/><circle cx="24" cy="29.5" r="2.4"/><circle cx="31" cy="29.5" r="2.4"/>
  </g>
</svg>`.trim();
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export default function WarRoom() {
  const { plantId } = usePlant();
  const [today] = useState(() => localToday());
  const [viewDate, setViewDate] = useState(() => localToday());
  const [cloneOpen, setCloneOpen] = useState(false);
  const [dash, setDash] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [trucks, setTrucks] = useState([]);          // /api/trucks
  const [tracks, setTracks] = useState([]);          // /api/trucks/tracks (live positions)
  const [mixes, setMixes] = useState([]);
  const [extraTags, setExtraTags] = useState([]);
  const [fleetHealth, setFleetHealth] = useState({});
  const { org } = useAuth();
  const fleet = isFleet(org);
  const isDemoOrg = !!org?.is_demo;
  const openPitch = async (e, j, which) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/jobs/${j.id}/pitch-links`);
      const path = data[`${which}_path`];
      if (!path) return toast.error(which === "driver" ? "Driver view is available in Demo Mode only" : "No link available for this job");
      window.open(`${window.location.origin}${path}`, "_blank", "noopener");
    } catch { toast.error("Could not build the link"); }
  };
  const [batching, setBatching] = useState(null);
  const announcedRef = useRef(null);   // batch ids already announced "Load Truck" (null = not primed yet)
  const [sysdyneTwoWay, setSysdyneTwoWay] = useState(false);
  useEffect(() => { api.get("/connectors").then((r) => setSysdyneTwoWay(!!r.data?.sysdyne?.enabled && r.data.sysdyne.mode === "bidirectional")).catch(() => {}); }, []);
  const sendToSysdyne = async (e, j) => {
    e.stopPropagation();
    try { const { data } = await api.post("/connectors/sysdyne/orders", { job_id: j.id }); toast.success(`Order queued for Sysdyne · ${data.state.toUpperCase()}`); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not queue order"); }
  };
  const sendToBatcher = async (e, j) => {
    e.stopPropagation();
    setBatching(j.id);
    try {
      const { data } = await api.post(`/jobs/${j.id}/one-touch-batch`, {});
      const b = data.batch || {};
      const plant = data.dispatch ? ` · sent to plant (${data.dispatch.filename})` : "";
      toast.success(`Load started · ${b.truck_number} · ${b.quantity_m3} m³ ${b.mix_code || ""}${plant}`);
      loadDash();
    } catch (err) {
      toast.error(err.response?.data?.detail || "One-touch batch failed");
    } finally { setBatching(null); }
  };
  // Fleet Dispatch — no batching: assign the next available truck and send it rolling.
  const assignNext = async (e, j) => {
    e.stopPropagation();
    const avail = trucks.find((t) => t.status === "available");
    if (!avail) { toast.error("No available trucks right now"); return; }
    setBatching(j.id);
    try {
      await api.post(`/jobs/${j.id}/assign-truck`, { truck_id: avail.id });
      toast.success(`${avail.number} assigned to ${j.customer_name}`);
      loadDash(); loadFleet();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not assign a truck");
    } finally { setBatching(null); }
  };
  // Quick Edit — compact in-War-Room order editor (essentials only; full editor stays on Dispatch).
  const [quickEditJob, setQuickEditJob] = useState(null);
  const [quickForm, setQuickForm] = useState(null);
  const [savingQuickEdit, setSavingQuickEdit] = useState(false);
  const openQuickEdit = (e, j) => {
    e.stopPropagation();
    setQuickEditJob(j);
    setQuickForm({
      date: j.date || viewDate,
      time_required: j.time_required || "",
      quantity_m3: j.quantity_m3 ?? "",
      mix_id: j.mix_id || "",
      po_number: j.po_number || "",
      notes: j.notes || "",
    });
  };
  const saveQuickEdit = async () => {
    if (!quickEditJob) return;
    setSavingQuickEdit(true);
    try {
      const payload = {
        date: quickForm.date,
        time_required: quickForm.time_required,
        quantity_m3: quickForm.quantity_m3 === "" ? null : parseFloat(quickForm.quantity_m3),
        po_number: quickForm.po_number,
        notes: quickForm.notes,
      };
      if (!fleet && quickForm.mix_id) payload.mix_id = quickForm.mix_id;
      await api.patch(`/jobs/${quickEditJob.id}`, payload, { offline: `Edit order · ${quickEditJob.customer_name || ""}` });
      toast.success(`Order for ${quickEditJob.customer_name || "customer"} updated`);
      setQuickEditJob(null);
      loadDash();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save changes");
    } finally { setSavingQuickEdit(false); }
  };
  const [predictions, setPredictions] = useState({});   // AI Turnaround Predictor · truck_id → prediction
  useEffect(() => {
    let dead = false;
    const pull = () => api.get(`/trucks/turnaround-predictions${plantId ? `?plant_id=${plantId}` : ""}`)
      .then((r) => { if (!dead) setPredictions(Object.fromEntries((r.data.predictions || []).map((p) => [p.truck_id, p]))); })
      .catch(() => {});
    pull();
    const t = setInterval(pull, 60_000);
    return () => { dead = true; clearInterval(t); };
  }, [plantId]);
  const [plantHQ, setPlantHQ] = useState(CAMBRIDGE);   // v3.15e · live plant coordinates from DB
  const [selectedTruckId, setSelectedTruckId] = useState(null);
  // v3.16 · High-speed click-to-assign flow. Click a job in Column 1, then a
  // truck in Column 2, and a floating confirm bar appears with `ASSIGN`. This
  // keeps drag-and-drop for power users but adds a two-tap alternative that's
  // easier to hit from across the office.
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  // Smooth-glide targets. `positions[id]` = the LATEST server sample.
  // `displayed[id]` = the currently rendered lat/lng, tweened each animation
  // frame toward its target. This gives us buttery movement between the 30 s
  // polls even without Directions-based interpolation.
  const [displayed, setDisplayed] = useState({});
  const targetsRef = useRef({});   // { [id]: {lat, lng, sampledAt} }
  const rafRef = useRef(null);

  const [quickJob, setQuickJob] = useState(false);
  const [gpsOpen, setGpsOpen] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const { isLoaded: gmLoaded } = useJsApiLoader({
    id: "gadtech-war-room",
    googleMapsApiKey: GOOGLE_KEY,
    libraries: GOOGLE_LIBRARIES,
  });

  // ─── Data loaders ────────────────────────────────────────────────────
  const loadDash = useCallback(async () => {
    try {
      const p = plantId ? `&plant_id=${plantId}` : "";
      const { data } = await api.get(`/dashboard?date=${viewDate}${p}`);
      setDash(data);
      setJobs(data.jobs || []);
      // Vocal completion · announce any one-touch batch that just reached LOAD TRUCK
      announceReady(announcedRef, (data.jobs || []).filter((j) => j.live_batch?.ready_at).map((j) => ({ id: j.live_batch.batch_id, truck_number: j.live_batch.truck_number })));
    } catch (e) { /* keep last-known good */ }
  }, [viewDate, plantId]);

  const loadFleet = useCallback(async () => {
    try {
      const [t, m, fh, plants, tags] = await Promise.all([
        api.get(`/trucks${plantId ? `?plant_id=${plantId}` : ""}`),
        api.get("/mixes"),
        api.get("/admin/fleet-health").catch(() => ({ data: [] })),
        api.get("/plants").catch(() => ({ data: [] })),
        api.get("/extra-tags").catch(() => ({ data: [] })),
      ]);
      setTrucks(t.data);
      setMixes(m.data);
      setExtraTags(tags.data || []);
      // v3.15e · Pick the currently-selected plant (or first active) and
      // pin the map + geofence marker to its real coordinates. Falls back
      // to the CAMBRIDGE constant if the DB has no lat/lng yet.
      const activePlant = (plants.data || []).find((p) => (plantId ? p.id === plantId : p.active !== false));
      if (activePlant && activePlant.lat && activePlant.lng) {
        setPlantHQ({ lat: activePlant.lat, lng: activePlant.lng, radius: activePlant.geofence_radius_m || 75, name: activePlant.name });
      }
      const map = {};
      (fh.data || []).forEach((r) => {
        map[r.truck.id] = {
          submitted: !!r.submitted_today,
          overall: r.latest_pre_start?.overall,
          resolved: !!r.latest_pre_start?.resolved,
        };
      });
      setFleetHealth(map);
    } catch (e) { /* ignore */ }
  }, [plantId]);

  // v3.11 · efficiency pass — default to active-only trucks on the map.
  // Available / Out-of-Service / Idle units don't need position tweening
  // and their `route_to_site` computation is skipped in the backend.
  const [mapView, setMapView] = useState(() => localStorage.getItem(MAP_VIEW_KEY) || "standard");
  const [showAllTrucks, setShowAllTrucks] = useState(false);
  const loadTracks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (plantId) params.set("plant_id", plantId);
      if (!showAllTrucks) params.set("active_only", "true");
      const qs = params.toString();
      const { data } = await api.get(`/trucks/tracks${qs ? "?" + qs : ""}`);
      setTracks(data);
      // Push new position targets into the tween buffer
      const next = { ...targetsRef.current };
      const now = Date.now();
      data.forEach((r) => {
        if (r.lat != null && r.lng != null) {
          next[r.id] = { lat: r.lat, lng: r.lng, sampledAt: now };
        }
      });
      targetsRef.current = next;
      // Seed displayed positions instantly for trucks we haven't seen before
      setDisplayed((prev) => {
        const seed = { ...prev };
        data.forEach((r) => {
          if (r.lat != null && r.lng != null && !seed[r.id]) {
            seed[r.id] = { lat: r.lat, lng: r.lng };
          }
        });
        return seed;
      });
    } catch (e) { /* ignore */ }
  }, [plantId, showAllTrucks]);

  useEffect(() => { loadDash(); loadFleet(); loadTracks(); }, [loadDash, loadFleet, loadTracks]);

  // Poll: dashboard/jobs every 60 s, tracks every 30 s (per brief).
  useEffect(() => {
    const t1 = setInterval(() => { if (!document.hidden) { loadDash(); loadFleet(); } }, 30_000);
    const t2 = setInterval(() => { if (!document.hidden) loadTracks(); }, 10_000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [loadDash, loadFleet, loadTracks]);

  // Live sync — any dispatch/driver event refreshes the board within ~250 ms.
  useLiveRefresh(() => { loadDash(); loadFleet(); loadTracks(); }, { intervalMs: 60_000, org: localStorage.getItem(SA_ORG_KEY) || null });

  // Spoken "truck back in yard" call-out (matches the loader screen). Arms audio on
  // the operator's first click so the browser allows speech.
  useEffect(() => { autoArmOnFirstGesture(); }, []);
  useEffect(() => {
    let ws, closed = false, retry = 0;
    const connect = () => {
      if (closed) return;
      try { ws = new WebSocket(`${WS_BASE}/ws/dispatch`); } catch { return; }
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (plantId && evt.plant_id && evt.plant_id !== plantId) return;
          if (evt.type === "truck.status" && evt.arrived_yard) { speakTruckBack(evt.truck_number); loadFleet(); }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { if (!closed) { retry = Math.min(retry + 1, 5); setTimeout(connect, 1000 * retry); } };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => { closed = true; try { ws && ws.close(); } catch { /* ignore */ } };
  }, [plantId, loadFleet]);

  // Send / clear a returning-truck instruction (Wash Out / Wash Fins / Dump Leftovers).
  const toggleWash = async (t, task) => {
    const active = !t.wash_tasks?.[task];
    try {
      await api.post(`/trucks/${t.id}/wash-task`, { task, active });
      loadFleet();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send instruction"); }
  };

  // Smooth-glide loop — tweens displayed markers toward the latest sample so
  // trucks slide across the map instead of jumping every 30 s.
  useEffect(() => {
    const tick = () => {
      const targets = targetsRef.current;
      setDisplayed((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const id of Object.keys(targets)) {
          const tgt = targets[id];
          const cur = prev[id];
          if (!cur) { next[id] = { lat: tgt.lat, lng: tgt.lng }; changed = true; continue; }
          const dLat = tgt.lat - cur.lat;
          const dLng = tgt.lng - cur.lng;
          if (Math.abs(dLat) < 1e-7 && Math.abs(dLng) < 1e-7) continue;
          // Ease ~5% per frame → converges in <1 s but always finishes.
          next[id] = { lat: cur.lat + dLat * 0.05, lng: cur.lng + dLng * 0.05 };
          changed = true;
        }
        return changed ? next : prev;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // ─── Derived data ────────────────────────────────────────────────────
  const truckById   = useMemo(() => Object.fromEntries(trucks.map((t) => [t.id, t])), [trucks]);
  const trackById   = useMemo(() => Object.fromEntries(tracks.map((r) => [r.id, r])), [tracks]);
  const jobById     = useMemo(() => Object.fromEntries(jobs.map((j) => [j.id, j])), [jobs]);
  const mixById     = useMemo(() => Object.fromEntries(mixes.map((m) => [m.id, m])), [mixes]);
  const mixByCode   = useMemo(() => Object.fromEntries(mixes.map((m) => [m.code, m])), [mixes]);

  const needsPrestartTrucks = trucks.filter((t) => needsPreStartFlag(fleetHealth[t.id]));

  const orderedJobs = useMemo(() => {
    const rank = { in_progress: 0, scheduled: 1, completed: 2, cancelled: 3 };
    return [...jobs].sort((a, b) =>
      (rank[a.status] ?? 5) - (rank[b.status] ?? 5) ||
      (a.time_required || "").localeCompare(b.time_required || ""),
    );
  }, [jobs]);
  // Finished work drops out of the live list and parks in a drawer at the bottom of the
  // column, so the board only shows what still needs a truck.
  const doneJobs = useMemo(() => orderedJobs.filter((j) => j.status === "completed" || j.status === "cancelled"), [orderedJobs]);
  const activeJobs = useMemo(() => orderedJobs.filter((j) => j.status !== "completed" && j.status !== "cancelled"), [orderedJobs]);
  const visibleJobs = showDone ? [...activeJobs, ...doneJobs] : activeJobs;

  const selectedTruck = selectedTruckId ? truckById[selectedTruckId] : null;
  const selectedTrack = selectedTruckId ? trackById[selectedTruckId] : null;
  const selectedJob = selectedTruck?.current_job_id ? jobById[selectedTruck.current_job_id] : null;
  const selectedMix = selectedJob ? (mixById[selectedJob.mix_id] || mixByCode[selectedJob.mix_code]) : null;
  const selectedLoad = selectedJob && selectedTruck
    ? (selectedJob.truck_loads?.[selectedTruck.id] ?? selectedTruck.capacity_m3)
    : selectedTruck?.capacity_m3;

  const mapCenter = useMemo(() => {
    // Prefer average of currently displayed truck positions; fall back to plant HQ.
    const pts = Object.values(displayed);
    if (!pts.length) return plantHQ;
    const lat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
    const lng = pts.reduce((s, p) => s + p.lng, 0) / pts.length;
    return { lat, lng };
  }, [displayed, plantHQ]);

  // Trucks heading back to the yard, with ETAs — same logic as the Loader screen,
  // surfaced as an always-on strip on the map so the dispatcher sees it at a glance.
  const returningRows = useMemo(() => {
    const yLat = plantHQ.lat, yLng = plantHQ.lng;
    return tracks
      .filter((t) => t.lat != null && t.lng != null && RETURNING_SET.has(t.status))
      .map((t) => {
        const dist = kmDist(t.lat, t.lng, yLat, yLng);
        const kph = t.speed_kph && t.speed_kph > 5 ? t.speed_kph : 45;
        let eta = Math.round((dist / kph) * 60), routed = false, at = null;
        if (/^\d{2}:\d{2}$/.test(t.eta_back_to_plant || "")) {
          const [h, m] = t.eta_back_to_plant.split(":").map(Number);
          const now = new Date(); let mins = h * 60 + m - (now.getHours() * 60 + now.getMinutes());
          if (mins < -60) mins += 1440;
          eta = Math.max(0, mins); routed = true; at = t.eta_back_to_plant;
        }
        return { ...t, dist, eta, routed, at };
      })
      .sort((a, b) => (a.status === "returning" ? 0 : 1) - (b.status === "returning" ? 0 : 1) || a.dist - b.dist);
  }, [tracks, plantHQ]);

  // v3.16 · Progress helper — mirrors the Dispatch board's three-state maths
  // so the war-room bar tells the same story.
  const IN_TRANSIT_STATES = new Set(["loading", "slumping", "en_route", "on_site", "discharging"]);
  const computeJobProgress = (j) => {
    const ordered = Number(j.quantity_m3 || 0) || 1;
    const delivered = Number(j.delivered_m3 || 0);
    // What's actually ON the truck — its booked load if we know it, otherwise what's
    // still owed on the job (capped by the barrel). Never the barrel size itself:
    // a 7 m³ truck taking the last 3.6 m³ is carrying 3.6, not 7.
    let left = Math.max(0, ordered - delivered);
    const inTransit = (j.assigned_truck_ids || []).reduce((s, tid) => {
      const t = truckById[tid];
      if (!t || !IN_TRANSIT_STATES.has(t.status)) return s;
      const explicit = j.truck_loads?.[tid];
      const cap = Number(t.capacity_m3 || 0);
      // Legacy rows stored the barrel size as the load — clamp to what's still owed.
      let v = explicit != null
        ? (left > 0 ? Math.min(Number(explicit), left) : Number(explicit))
        : Math.min(left || cap, cap || left);
      if (!Number.isFinite(v) || v < 0) v = 0;
      left = Math.max(0, left - v);
      return s + v;
    }, 0);
    const committed = delivered + inTransit;
    return {
      ordered,
      delivered,
      inTransit,
      overOrder: committed > ordered + 0.001,
      deliveredPct: Math.min(100, (delivered / ordered) * 100),
      inTransitPct: Math.max(0, Math.min(100 - Math.min(100, (delivered / ordered) * 100), (inTransit / ordered) * 100)),
      pct: Math.min(100, (committed / ordered) * 100),
    };
  };

  // Shared assign — used by both the two-tap flow and drag-and-drop. Posts to
  // assign-truck, which applies the AI capacity-aware load split automatically.
  const assignTruckToJob = useCallback(async (jobId, truckId) => {
    if (!jobId || !truckId) return;
    setAssigning(true);
    try {
      const t = truckById[truckId];
      const j = jobById[jobId];
      await api.post(`/jobs/${jobId}/assign-truck`, { truck_id: truckId });
      toast.success(`${t?.number || "truck"} → ${j?.customer_name || "job"} · assigned`);
      setSelectedJobId(null);
      setSelectedTruckId(null);
      loadDash(); loadFleet();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assign failed");
    } finally {
      setAssigning(false);
    }
  }, [truckById, jobById, loadDash, loadFleet]);

  // Two-tap assign flow. Assumes both selectedJobId + selectedTruckId are set.
  const assignSelected = useCallback(
    () => assignTruckToJob(selectedJobId, selectedTruckId),
    [assignTruckToJob, selectedJobId, selectedTruckId]
  );

  // Drag-and-drop assign · drag a truck card from the fleet column onto a
  // customer/job card to assign it (same AI load-split as the two-tap flow).
  const [dragTruckId, setDragTruckId] = useState(null);
  const [dropJobId, setDropJobId] = useState(null);

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] min-h-0 w-full max-w-full overflow-hidden bg-[rgb(var(--bg))]" data-testid="war-room-page">
      <WeatherReplyWatcher />
      <QuickJobDrawer open={quickJob} onOpenChange={setQuickJob} plantId={plantId} date={viewDate}
        onCreated={() => { loadDash(); loadFleet(); }} />
      <LoaderGpsDialog open={gpsOpen} onOpenChange={setGpsOpen} />
      {/* ── Quick Edit order dialog ─────────────────────────────────── */}
      <Dialog open={!!quickEditJob} onOpenChange={(v) => { if (!v) setQuickEditJob(null); }}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-md" data-testid="war-room-quick-edit-dialog">
          <DialogHeader>
            <DialogTitle className="mono uppercase tracking-widest text-sm">
              Quick edit · <span className="text-primary-orange">{quickEditJob?.customer_name}</span>
            </DialogTitle>
          </DialogHeader>
          {quickForm && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Date</Label>
                  <Input data-testid="qe-date" type="date" value={quickForm.date}
                    onChange={(e) => setQuickForm({ ...quickForm, date: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <div>
                  <Label className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Time required</Label>
                  <Input data-testid="qe-time" type="time" value={quickForm.time_required}
                    onChange={(e) => setQuickForm({ ...quickForm, time_required: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono" />
                </div>
                <div>
                  <Label className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Quantity (m³)</Label>
                  <Input data-testid="qe-qty" type="number" step="0.2" value={quickForm.quantity_m3}
                    onChange={(e) => setQuickForm({ ...quickForm, quantity_m3: e.target.value })}
                    className="rounded-none bg-transparent border-industrial mono" />
                </div>
                {!fleet && (
                  <div>
                    <Label className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Mix</Label>
                    <Select value={quickForm.mix_id || ""} onValueChange={(v) => setQuickForm({ ...quickForm, mix_id: v })}>
                      <SelectTrigger data-testid="qe-mix" className="rounded-none bg-transparent border-industrial mono">
                        <SelectValue placeholder="Select mix" />
                      </SelectTrigger>
                      <SelectContent>
                        {mixes.map((m) => (<SelectItem key={m.id} value={m.id}>{m.code}{m.strength_mpa ? ` · ${m.strength_mpa}MPa` : ""}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div>
                <Label className="mono text-[10px] uppercase tracking-widest text-muted-industrial">PO number</Label>
                <Input data-testid="qe-po" value={quickForm.po_number}
                  onChange={(e) => setQuickForm({ ...quickForm, po_number: e.target.value })}
                  className="rounded-none bg-transparent border-industrial mono" />
              </div>
              <div>
                <Label className="mono text-[10px] uppercase tracking-widest text-muted-industrial">Delivery notes</Label>
                <Textarea data-testid="qe-notes" rows={2} value={quickForm.notes}
                  onChange={(e) => setQuickForm({ ...quickForm, notes: e.target.value })}
                  className="rounded-none bg-transparent border-industrial" />
              </div>
              <p className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Need to switch customer or reprice? Use the full editor on the Dispatch screen.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="qe-cancel" onClick={() => setQuickEditJob(null)}
              className="rounded-none border-industrial bg-transparent uppercase text-xs tracking-widest">Cancel</Button>
            <Button data-testid="qe-save" onClick={saveQuickEdit} disabled={savingQuickEdit}
              className="rounded-none bg-primary-orange text-black uppercase text-xs tracking-widest font-bold">
              {savingQuickEdit ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ── Sticky KPI strip ────────────────────────────────────────── */}
      <div
        className="sticky top-0 z-30 border-b border-industrial bg-surface/95 backdrop-blur px-4 py-3 flex flex-wrap items-center gap-6"
        data-testid="war-room-kpi-strip"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-none bg-primary-orange flex items-center justify-center">
            <Radar className="w-4 h-4 text-black" strokeWidth={2.5} />
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.28em] text-muted-industrial">5 AM · Command</div>
            <div className="heading text-sm font-black">WAR ROOM</div>
          </div>
          <a href="/dispatch?classic=1" data-testid="war-room-classic-toggle" title="Switch to Classic Dispatch board"
            className="ml-2 mono text-[9px] uppercase tracking-widest px-2 py-1 border border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange">
            Classic Dispatch
          </a>
          <button type="button" onClick={() => setQuickJob(true)} data-testid="war-room-new-job" title="Schedule a new job"
            className="ml-1 inline-flex items-center gap-1 mono text-[9px] uppercase tracking-widest font-black px-2 py-1 bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))]">
            <Plus className="w-3 h-3" strokeWidth={3} /> New Job
          </button>
          <PrintScheduleButton plantId={plantId} className="ml-1 !text-[9px] !py-1 !text-muted-industrial" />
          <PriceCalcPanel className="ml-1" />
        </div>
        <div className="h-8 w-px bg-industrial hidden sm:block" />
        <div className="flex items-center gap-2" data-testid="war-room-kpi-volume">
          <Package className="w-4 h-4 text-primary-orange" />
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Today's Volume</div>
            <div className="heading text-xl font-black leading-none">
              {(dash?.production_m3 ?? 0).toFixed(1)}<span className="text-xs text-muted-industrial ml-1">m³</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="war-room-kpi-revenue">
          <DollarSign className="w-4 h-4 text-emerald-500" />
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Today's Revenue</div>
            <div className="heading text-xl font-black leading-none text-emerald-500">
              {money(dash?.revenue_today)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto text-xs text-muted-industrial mono uppercase tracking-widest">
          <Radio className={`w-3.5 h-3.5 ${tracks.length ? "text-emerald-500 animate-pulse" : "text-muted-industrial"}`} />
          {tracks.length} live · {trucks.length} fleet · {orderedJobs.length} jobs
          {/* Date picker — view & plan any day */}
          <div className="flex items-center gap-1 ml-2" data-testid="war-room-date-controls">
            <button type="button" onClick={() => setViewDate(shiftLocalDate(viewDate, -1))} data-testid="war-room-date-prev"
              className="w-6 h-6 flex items-center justify-center border border-industrial hover:border-primary-orange hover:text-primary-orange">‹</button>
            <input type="date" value={viewDate} onChange={(e) => setViewDate(e.target.value || today)} data-testid="war-room-view-date"
              className="bg-surface-2 border border-industrial px-1.5 py-1 mono text-[10px] text-ink focus:border-primary-orange outline-none" />
            <button type="button" onClick={() => setViewDate(shiftLocalDate(viewDate, 1))} data-testid="war-room-date-next"
              className="w-6 h-6 flex items-center justify-center border border-industrial hover:border-primary-orange hover:text-primary-orange">›</button>
            {viewDate !== today && (
              <button type="button" onClick={() => setViewDate(today)} data-testid="war-room-date-today"
                className="px-1.5 h-6 border border-primary-orange text-primary-orange hover:bg-primary-orange/10 text-[9px]">Today</button>
            )}
          </div>
          <button type="button" onClick={() => setCloneOpen(true)} data-testid="war-room-clone-orders" title="Clone multiple orders to another day"
            className="inline-flex items-center gap-1 px-2 h-6 border border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange text-[9px]">
            <Copy className="w-3 h-3" /> Clone orders
          </button>
        </div>
      </div>

      <BulkCloneDialog open={cloneOpen} onOpenChange={setCloneOpen} plantId={plantId} initialDate={viewDate} onDone={() => { loadDash(); }} />

      {/* ── 3-column body (33/33/33 split) ─────────────────────────── */}
      <div className="flex-1 min-h-0 w-full flex flex-col lg:flex-row lg:flex-nowrap gap-3 p-3 overflow-y-auto lg:overflow-hidden" data-testid="war-room-columns">
        {/* LEFT · Jobs list */}
        <div className="card-industrial flex flex-col overflow-hidden w-full min-h-[60vh] lg:min-h-0 lg:basis-0 lg:grow-[2] lg:shrink min-w-0 shrink-0 grow-0" data-testid="war-room-jobs">
          <div className="px-3 py-2 border-b border-industrial flex items-center justify-between">
            <div className="label-tag">Jobs · {viewDate === today ? "Today" : formatLocalDate(viewDate)}</div>
            <span className="mono text-[10px] uppercase tracking-widest text-primary-orange">
              {activeJobs.length}{doneJobs.length ? <span className="text-muted-industrial"> · {doneJobs.length} done</span> : null}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-industrial">
            {activeJobs.length === 0 && (
              <div className="p-6 text-center text-muted-industrial text-xs">
                {doneJobs.length ? "Every job's done for today." : "No jobs booked for today."}
              </div>
            )}
            {visibleJobs.map((j) => {
              const isDone = j.status === "completed";
              const running = j.status === "in_progress";
              const assigned = (j.assigned_truck_ids || []).length;
              const p = computeJobProgress(j);
              const active = selectedJobId === j.id;
              return (
                <button
                  key={j.id}
                  type="button"
                  data-testid={`war-room-job-${j.id}`}
                  onClick={() => setSelectedJobId(active ? null : j.id)}
                  onDragOver={(e) => {
                    if (!dragTruckId) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (dropJobId !== j.id) setDropJobId(j.id);
                  }}
                  onDragLeave={() => { if (dropJobId === j.id) setDropJobId(null); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const tid = dragTruckId || e.dataTransfer.getData("text/plain");
                    setDropJobId(null);
                    setDragTruckId(null);
                    if (tid) assignTruckToJob(j.id, tid);
                  }}
                  className={`w-full text-left px-3 py-2.5 hover:bg-surface-2/50 transition-colors border-l-2 ${
                    dropJobId === j.id
                      ? "bg-primary-orange/20 border-primary-orange ring-2 ring-primary-orange ring-inset"
                      : active
                      ? "bg-primary-orange/10 border-primary-orange"
                      : running
                        ? "border-primary-orange bg-surface-2/30"
                        : "border-transparent"
                  } ${isDone ? "opacity-55" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="mono text-[10px] uppercase tracking-widest text-primary-orange">
                      {j.time_required || "—"}
                    </span>
                    <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-industrial text-muted-industrial">
                      {(j.status || "").replace("_", " ")}
                    </span>
                  </div>
                  <div className="font-bold text-sm truncate mt-1">{j.customer_name}</div>
                  <div className="text-[11px] text-muted-industrial truncate">
                    {j.delivery_address || j.site_address || j.site_name || "—"}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-industrial">
                    <span className="mono text-ink font-bold">{p.ordered} m³</span>
                    <span className="mono">·</span>
                    <span className="mono">{j.mix_code}</span>
                    <span className="ml-auto flex items-center gap-1">
                      <TruckIcon className="w-3 h-3" />{assigned}
                    </span>
                  </div>
                  {/* Customer's answer to the pour-conditions warning text — a
                      green tick once they've accepted, red until they answer. */}
                  {(j.weather_reply || j.weather_alert_sent_at) && (() => {
                    const code = j.weather_reply?.code;
                    const style = code === "go_ahead" ? "border-emerald-500 text-emerald-400 bg-emerald-500/10"
                      : code === "delay" ? "border-red-500 text-red-400 bg-red-500/10 animate-pulse"
                        : code === "call_me" ? "border-amber-500 text-amber-400 bg-amber-500/10"
                          : "border-red-500 text-red-400 bg-red-500/10";
                    const Icon = code === "go_ahead" ? Check : code === "delay" ? Clock : code === "call_me" ? Phone : AlertTriangle;
                    const text = code ? `${j.weather_reply.label} · customer` : "Warned · no answer yet";
                    const tip = code
                      ? `Customer replied "${j.weather_reply.text}" via ${j.weather_reply.via === "web" ? "the web link" : "SMS"}`
                      : "Pour-conditions warning sent — the customer hasn't answered yet. Ring them before the truck leaves.";
                    return (
                      <div data-testid={`war-room-weather-reply-${j.id}`} title={tip}
                        className={`mt-1.5 inline-flex items-center gap-1 mono text-[10px] font-black uppercase tracking-widest px-2 py-0.5 border ${style}`}>
                        <Icon className="w-3 h-3" /> {text}
                      </div>
                    );
                  })()}
                  {/* Extras + comments — big and bold so nobody misses a fibre/retarder/pump note */}
                  {((j.extra_tag_ids || []).length > 0 || j.extras || j.notes) && (
                    <div className="mt-1.5 border-l-4 border-[#FBBF24] bg-[#FBBF24]/10 px-2 py-1.5 space-y-1" data-testid={`war-room-extras-${j.id}`}>
                      {(j.extra_tag_ids || []).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {(j.extra_tag_ids || []).map((tid) => { const t = extraTags.find((x) => x.id === tid); return t ? (
                            <span key={tid} className="mono text-[11px] uppercase tracking-widest px-1.5 py-0.5 font-black text-white" style={{ background: t.color }}>{t.name}</span>
                          ) : null; })}
                        </div>
                      )}
                      {j.extras && <div className="text-sm font-black text-[#FBBF24] uppercase leading-tight break-words">{j.extras}</div>}
                      {j.notes && <div className="text-sm font-bold text-ink leading-tight break-words">{j.notes}</div>}
                    </div>
                  )}
                  {/* Pitch Kit · View as Customer / Pump / Driver */}
                  <div className="mt-2 flex flex-wrap items-center gap-1" data-testid={`pitch-kit-${j.id}`}>
                    <span className="mono text-[9px] uppercase tracking-widest text-muted-industrial mr-1">View as</span>
                    <span role="button" tabIndex={0} data-testid={`view-as-customer-${j.id}`} onClick={(e) => openPitch(e, j, "customer")}
                      className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange cursor-pointer">
                      <Eye className="w-3 h-3" /> Customer
                    </span>
                    <span role="button" tabIndex={0} data-testid={`view-as-pump-${j.id}`} onClick={(e) => openPitch(e, j, "pump")}
                      className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange cursor-pointer">
                      <HardHat className="w-3 h-3" /> Pump
                    </span>
                    {isDemoOrg && (
                      <span role="button" tabIndex={0} data-testid={`view-as-driver-${j.id}`} onClick={(e) => openPitch(e, j, "driver")}
                        className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-sky-500 text-sky-400 hover:bg-sky-500 hover:text-black cursor-pointer">
                        <Smartphone className="w-3 h-3" /> Driver
                      </span>
                    )}
                    <button type="button" data-testid={`war-room-edit-job-${j.id}`} onClick={(e) => openQuickEdit(e, j)}
                      title="Quick edit — date, time, quantity, mix, PO & notes (no need to open Dispatch)"
                      className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange cursor-pointer">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <span className="ml-auto inline-flex flex-wrap items-center justify-end gap-1">
                      {j.live_batch ? (
                        <span data-testid={`batch-status-${j.id}`}
                          title={j.live_batch.ready_at ? "All weights on target & stable — load complete" : `${j.live_batch.weighed}/${j.live_batch.materials} materials weighed`}
                          className={`inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${j.live_batch.ready_at ? "border-emerald-500 bg-emerald-500/15 text-emerald-300 animate-pulse" : "border-amber-500 text-amber-300"}`}>
                          {j.live_batch.ready_at ? <><Check className="w-3 h-3" /> Load Complete · {j.live_batch.truck_number}</> : <>Weighing {j.live_batch.truck_number} · {j.live_batch.weighed}/{j.live_batch.materials}</>}
                        </span>
                      ) : (j.batch_total || 0) > 0 && (
                        <span data-testid={`batch-status-${j.id}`}
                          title={`${j.batch_sent} of ${j.batch_total} loads sent to batcher`}
                          className={`inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${j.batch_sent >= j.batch_total ? "border-emerald-500 text-emerald-400" : j.batch_sent > 0 ? "border-blue-500 text-blue-300" : "border-industrial text-muted-industrial"}`}>
                          {j.batch_sent > 0 && <Check className="w-3 h-3" />} Sent {j.batch_sent}/{j.batch_total} loads
                        </span>
                      )}
                      {sysdyneTwoWay && !fleet && (
                        <button type="button" data-testid={`send-to-sysdyne-${j.id}`} onClick={(e) => sendToSysdyne(e, j)} title="Queue this order as a Sysdyne ConcreteGO ticket"
                          className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-industrial text-ink hover:border-blue-500 cursor-pointer">Sysdyne</button>
                      )}
                      {fleet ? (
                        <button type="button" disabled={batching === j.id} data-testid={`assign-truck-${j.id}`} onClick={(e) => assignNext(e, j)}
                          title="Assign the next available truck and send it rolling"
                          className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-blue-600 border border-blue-500 text-white hover:bg-blue-500 disabled:opacity-50 cursor-pointer">
                          <TruckIcon className={`w-3 h-3 ${batching === j.id ? "animate-pulse" : ""}`} /> Assign
                        </button>
                      ) : (
                        <button type="button" disabled={batching === j.id || !!j.live_batch} data-testid={`send-to-batcher-${j.id}`} onClick={(e) => sendToBatcher(e, j)}
                          title={j.live_batch ? "A load is already in progress on this job" : "One-touch: start load + queue plant computer"}
                          className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-blue-600 border border-blue-500 text-white hover:bg-blue-500 disabled:opacity-50 cursor-pointer">
                          <Zap className={`w-3 h-3 ${batching === j.id ? "animate-pulse" : ""}`} /> Batch
                        </button>
                      )}
                    </span>
                  </div>
                  {/* Simplified progress bar + one-line stats — mirrors Dispatch */}
                  <div className="mt-2 flex flex-col gap-1">
                    <div
                      className={`h-2 border flex overflow-hidden ${p.overOrder ? "border-red-500 progressive-over-order" : "border-industrial"} bg-surface-2`}
                      title={`Delivered ${p.delivered.toFixed(1)}m³ · In transit ${p.inTransit.toFixed(1)}m³`}
                    >
                      <div
                        className={`h-full transition-all ${p.overOrder ? "bg-red-500" : "bg-primary-orange"}`}
                        style={{ width: `${p.deliveredPct}%` }}
                      />
                      <div
                        className={`h-full transition-all ${p.overOrder ? "bg-red-500/50" : "bg-primary-orange/35"}`}
                        style={{ width: `${p.inTransitPct}%` }}
                      />
                    </div>
                    <div className="mono text-[10px] flex items-center gap-1.5">
                      <span className="text-ink font-bold">{p.delivered.toFixed(1)}</span>
                      <span className="text-muted-industrial">/</span>
                      <span className="text-ink">{p.ordered}m³</span>
                      <span className="text-muted-industrial">·</span>
                      <span className={p.overOrder ? "text-red-500 font-bold" : "text-primary-orange font-bold"}>
                        {p.pct.toFixed(0)}%
                      </span>
                      {p.inTransit > 0 && (
                        <>
                          <span className="text-muted-industrial">·</span>
                          <span className={p.overOrder ? "text-red-500" : "text-primary-orange/80"}>
                            {p.inTransit.toFixed(1)}m³ in transit
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {doneJobs.length > 0 && (
            <button type="button" onClick={() => setShowDone(!showDone)} data-testid="war-room-completed-toggle"
              className="shrink-0 border-t border-industrial px-3 py-2 flex items-center gap-2 bg-surface-2/40 hover:bg-surface-2 text-left">
              <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span className="mono text-[10px] uppercase tracking-widest text-muted-industrial">
                Completed jobs · {doneJobs.length}
              </span>
              <span className="mono text-[10px] uppercase tracking-widest text-primary-orange ml-auto">
                {showDone ? "Hide" : "Show"}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 text-muted-industrial transition-transform ${showDone ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>

        {/* MIDDLE · Fleet Status w/ Safety Lockout */}
        <div className="card-industrial flex flex-col overflow-hidden w-full min-h-[60vh] lg:min-h-0 lg:basis-0 lg:grow-[1] lg:shrink min-w-0 shrink-0 grow-0" data-testid="war-room-fleet">
          <div className="px-3 py-2 border-b border-industrial flex items-center justify-between">
            <div className="label-tag">Fleet · Safety</div>
            <span className={`mono text-[10px] uppercase tracking-widest ${needsPrestartTrucks.length ? "text-red-400 flash-red" : "text-emerald-500"}`}>
              {needsPrestartTrucks.length
                ? `${needsPrestartTrucks.length} lockout`
                : "all clear"}
            </span>
          </div>
          {needsPrestartTrucks.length > 0 && (
            <div
              className="mx-3 mt-3 border border-red-600 bg-red-950/40 text-red-200 p-2 text-[11px] flex items-start gap-2"
              data-testid="war-room-safety-lockout"
            >
              <ShieldOff className="w-4 h-4 mt-0.5 shrink-0 animate-pulse text-red-400" />
              <div>
                <div className="mono uppercase tracking-widest text-red-300 text-[10px]">Safety Lockout Active</div>
                <div className="mt-0.5">
                  {needsPrestartTrucks.length} truck{needsPrestartTrucks.length > 1 ? "s" : ""} missing today's pre-start. Dispatch is
                  blocked until submitted or manually overridden on the Dispatch board.
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {trucks.length === 0 && (
              <div className="p-6 text-center text-muted-industrial text-xs">No trucks configured.</div>
            )}
            {trucks.map((t) => {
              const ps = fleetHealth[t.id];
              const needsPS = needsPreStartFlag(ps);
              const tint = STATUS_TINT[t.status] || "#71717A";
              const active = selectedTruckId === t.id;
              const tr = trackById[t.id];
              const job = t.current_job_id ? jobById[t.current_job_id] : null;
              const goneDark = ON_ROAD.has(t.status) && (!tr || (tr.location_age_s ?? 1e9) > DARK_AFTER_S);
              const flashRgb = goneDark ? DARK_RGB : FLASH_RGB[t.status];
              return (
                <div key={t.id} className="border-b border-industrial/40 last:border-0">
                <button
                  type="button"
                  draggable
                  onDragStart={(e) => {
                    setDragTruckId(t.id);
                    e.dataTransfer.effectAllowed = "move";
                    try { e.dataTransfer.setData("text/plain", t.id); } catch { /* ignore */ }
                  }}
                  onDragEnd={() => { setDragTruckId(null); setDropJobId(null); }}
                  onClick={() => setSelectedTruckId(t.id === selectedTruckId ? null : t.id)}
                  data-testid={`war-room-truck-${t.number}`}
                  style={flashRgb && !needsPS ? { "--flash-rgb": flashRgb } : undefined}
                  className={`w-full text-left border-2 bg-surface-2/40 hover:bg-surface-2 px-2.5 py-2 flex items-center gap-2 transition-colors cursor-grab active:cursor-grabbing ${
                    dragTruckId === t.id ? "opacity-50 ring-2 ring-primary-orange" : ""
                  } ${
                    needsPS ? "pulse-red-strong border-red-500"
                      : goneDark ? "flash-status-fast border-red-500"
                      : flashRgb ? "flash-status border-industrial"
                      : "border-industrial"
                  } ${active ? "!border-primary-orange" : ""}`}
                >
                  <TruckIcon className="w-4 h-4 shrink-0" style={{ color: needsPS ? "#EF4444" : tint }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`mono text-sm font-bold ${needsPS ? "text-red-400 flash-red" : ""}`}>
                        {t.number}
                      </span>
                      <span
                        className="mono text-[8px] uppercase tracking-widest px-1 py-0.5 border"
                        style={{ borderColor: tint, color: tint }}
                      >
                        {(t.status || "").replace("_", " ")}
                      </span>
                      {goneDark && !needsPS && (
                        <span data-testid={`war-room-dark-${t.number}`} title="No GPS ping for over 3 minutes while out on the road"
                          className="mono text-[8px] uppercase tracking-widest px-1 py-0.5 border border-red-500 text-red-400 flash-red">
                          Gone Dark
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-industrial truncate flex items-center gap-1">
                      <span className="truncate">{t.driver_name || "no driver"} · {t.capacity_m3} m³{tr?.speed_kph != null && ` · ${Math.round(tr.speed_kph)} km/h`}</span>
                      {!job && predictions[t.id] && (
                        <span data-testid={`war-room-predicted-return-${t.number}`} title={`AI Turnaround Predictor · ${predictions[t.id].basis} · ${predictions[t.id].reason}`}
                          className={`mono uppercase tracking-widest ml-auto px-1 py-0.5 border text-[9px] shrink-0 ${predictions[t.id].confidence === "low" ? "border-industrial text-muted-industrial" : "border-emerald-600/60 text-emerald-400"}`}>
                          ↺ {predictions[t.id].predicted_return_local}
                        </span>
                      )}
                    </div>
                    {job && (() => {
                      const eta = job.truck_etas?.[t.id];
                      const etaLabel = eta?.eta_to_site || eta?.eta_back_to_plant;
                      return (
                        <div className="flex items-center gap-1.5 mt-0.5 text-[10px]">
                          <span className="text-primary-orange truncate">
                            {job.mix_code} → {job.customer_name}
                          </span>
                          {etaLabel && (
                            <span className="mono uppercase tracking-widest ml-auto px-1 py-0.5 border border-industrial text-primary-orange">
                              <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                              {etaLabel}
                            </span>
                          )}
                          {predictions[t.id] && (
                            <span data-testid={`war-room-predicted-return-${t.number}`}
                              title={`AI Turnaround Predictor · ${predictions[t.id].basis} · ${predictions[t.id].reason}`}
                              className={`mono uppercase tracking-widest px-1 py-0.5 border ${etaLabel ? "" : "ml-auto"} ${predictions[t.id].confidence === "high" ? "border-emerald-500 text-emerald-400" : predictions[t.id].confidence === "medium" ? "border-sky-500 text-sky-400" : "border-industrial text-muted-industrial"}`}>
                              ↺ {predictions[t.id].predicted_return_local}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  {needsPS && (
                    <AlertTriangle className="w-4 h-4 text-red-400 animate-pulse" data-testid={`war-room-lockout-${t.number}`} />
                  )}
                  <ChevronRight className="w-3.5 h-3.5 text-muted-industrial" />
                </button>
                {!fleet && (
                <div className="flex items-center gap-1 px-2.5 pb-1.5 pt-0.5 flex-wrap" data-testid={`wash-tasks-${t.number}`}>
                  {[["wash_out", "Wash Out"], ["wash_fins", "Wash Fins"], ["dump_leftovers", "Dump"]].map(([k, label]) => {
                    const task = t.wash_tasks?.[k];
                    const on = !!task;
                    const acked = !!task?.acked_at;
                    return (
                      <button key={k} type="button" onClick={(e) => { e.stopPropagation(); toggleWash(t, k); }}
                        data-testid={`wash-${k}-${t.number}`}
                        title={on ? (acked ? `${label} — driver acknowledged` : `${label} sent — waiting for driver`) : `Send "${label}" to ${t.driver_name || "driver"}`}
                        className={`mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border transition-colors ${
                          on
                            ? acked
                              ? "border-emerald-500 text-emerald-400"
                              : "border-primary-orange text-primary-orange bg-primary-orange/10 animate-pulse"
                            : "border-industrial text-muted-industrial hover:border-primary-orange hover:text-primary-orange"
                        }`}>
                        {acked ? "✓ " : ""}{label}
                      </button>
                    );
                  })}
                </div>
                )}
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT · Live Google Map */}
        <div className="card-industrial overflow-hidden relative w-full h-[60vh] lg:h-full lg:basis-0 lg:grow-[3] lg:shrink min-w-0 shrink-0 grow-0" data-testid="war-room-map">
          {/* Map view switcher — normal Google colours by default, dark / satellite optional */}
          <div className="absolute top-2 left-2 z-10 flex bg-[#0B0C0E] border border-industrial shadow-lg" data-testid="war-room-map-view">
            {[["standard", "Map"], ["dark", "Dark"], ["satellite", "Satellite"]].map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => { setMapView(v); localStorage.setItem(MAP_VIEW_KEY, v); }}
                data-testid={`war-room-map-view-${v}`}
                className={`mono text-[10px] uppercase tracking-widest px-2 py-1 transition-colors ${mapView === v ? "bg-primary-orange text-black font-bold" : "text-white/80 hover:text-primary-orange"}`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Trucks Back — same returning-ETA view as the Loader screen */}
          <button type="button" onClick={() => setGpsOpen(true)} data-testid="war-room-trucks-back"
            title="Live trucks heading back to the yard, with returning ETAs — same view as the Loader screen"
            className="absolute top-2 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 bg-[#0B0C0E] border border-industrial px-2.5 py-1 shadow-lg mono text-[10px] uppercase tracking-widest text-white hover:border-primary-orange hover:text-primary-orange">
            <MapPin className="w-3 h-3" /> Trucks Back
          </button>
          {/* Slim always-on returning-trucks strip — ETAs without opening the dialog */}
          {returningRows.length > 0 && (
            <div className="absolute top-12 left-2 z-10 w-44 max-h-[62%] overflow-y-auto bg-black/80 border border-industrial shadow-lg" data-testid="war-room-returning-strip">
              <button type="button" onClick={() => setGpsOpen(true)} title="Open the full returning-trucks map"
                className="w-full px-2 py-1 border-b border-industrial flex items-center gap-1 sticky top-0 bg-black/90 hover:border-primary-orange">
                <MapPin className="w-3 h-3 text-sky-400" />
                <span className="mono text-[9px] uppercase tracking-widest text-sky-400">Returning · {returningRows.length}</span>
                <ChevronRight className="w-3 h-3 text-muted-industrial ml-auto" />
              </button>
              {returningRows.map((t) => (
                <div key={t.id} data-testid={`war-room-returning-${t.number}`} className="px-2 py-1.5 border-b border-industrial/50 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_TINT[t.status] || "#F97316" }} />
                  <div className="min-w-0 flex-1">
                    <div className="mono text-[11px] font-black text-white leading-none truncate">{t.number}</div>
                    <div className="mono text-[8px] uppercase tracking-widest leading-none mt-0.5" style={{ color: STATUS_TINT[t.status] || "#F97316" }}>{(t.status || "").replace("_", " ")}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="mono tabular-nums font-black text-sm leading-none text-primary-orange">{t.eta}<span className="text-[8px] text-muted-industrial ml-0.5">m</span></div>
                    {t.routed && <div className="mono text-[7px] text-emerald-400 leading-none mt-0.5">back {t.at}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* v3.11 · efficiency toggle — default hides inactive trucks */}
          <label
            className="absolute top-2 right-2 z-10 flex items-center gap-1.5 bg-black/70 border border-industrial px-2 py-1 cursor-pointer hover:border-primary-orange transition"
            data-testid="war-room-show-all-toggle"
            title="When OFF, only trucks that are loading, en-route, on-site, discharging or returning are drawn. Available/Out-of-Service trucks are skipped to save GPS/API load."
          >
            <input
              type="checkbox"
              checked={showAllTrucks}
              onChange={(e) => setShowAllTrucks(e.target.checked)}
              data-testid="war-room-show-all-cb"
              className="w-3 h-3"
            />
            <span className="mono text-[10px] uppercase tracking-widest text-ink">Show all trucks</span>
          </label>
          {!GOOGLE_KEY && (
            <div className="absolute inset-0 flex items-center justify-center text-red-400 text-sm mono">
              REACT_APP_GOOGLE_MAPS_API_KEY missing
            </div>
          )}
          {GOOGLE_KEY && !gmLoaded && (
            <div className="absolute inset-0 flex items-center justify-center text-muted-industrial text-xs mono">
              Loading Google Maps…
            </div>
          )}
          {GOOGLE_KEY && gmLoaded && (
            <MapErrorBoundary>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={12}
              mapTypeId={mapView === "satellite" ? "hybrid" : "roadmap"}
              options={{
                disableDefaultUI: false,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: true,
                zoomControl: true,
                clickableIcons: false,
                styles: mapView === "dark" ? WAR_ROOM_MAP_STYLE : [],
                backgroundColor: mapView === "standard" ? "#E5E3DF" : "#1F2126",
              }}
              onLoad={() => setMapReady(true)}
            >
              <TrafficLayer autoUpdate />
              {/* Plant marker (pinned to the live plant record) */}
              <OverlayView position={plantHQ} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                <div
                  className="translate-x-[-50%] translate-y-[-50%] pointer-events-none"
                  data-testid="war-room-plant-marker"
                  title={plantHQ.name || org?.name || "ArmorBatch"}
                >
                  <div className="w-6 h-6 bg-primary-orange border-2 border-black flex items-center justify-center shadow-md">
                    <Factory className="w-3 h-3 text-black" />
                  </div>
                </div>
              </OverlayView>

              {/* Route polylines (Google-routed, so already snapped to roads) */}
              {tracks.map((r) => {
                const poly = r.route_to_site;
                if (!poly || poly.length < 2) return null;
                return (
                  <Polyline
                    key={`route-${r.id}`}
                    path={poly.map(([lat, lng]) => ({ lat, lng }))}
                    options={{
                      strokeColor: STATUS_TINT[r.status] || "#F97316",
                      strokeOpacity: 0.85,
                      strokeWeight: 4,
                      clickable: false,
                    }}
                  />
                );
              })}

              {/* Trucks — smooth-glide OverlayView markers */}
              {trucks.map((t) => {
                const p = displayed[t.id];
                if (!p) return null;
                const isSelected = selectedTruckId === t.id;
                const ps = fleetHealth[t.id];
                const needsPS = needsPreStartFlag(ps);
                const tint = needsPS ? "#EF4444" : (STATUS_TINT[t.status] || "#F97316");
                const trk = tracks.find((x) => x.id === t.id) || {};
                const ageS = trk.location_age_s;
                const stale = trk.location_source === "simulated" || (typeof ageS === "number" && ageS > 300);
                const ageLabel = typeof ageS === "number" ? (ageS < 90 ? `${ageS}s ago` : `${Math.round(ageS / 60)} min ago`) : "no fix";
                return (
                  <OverlayView
                    key={`truck-${t.id}`}
                    position={p}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedTruckId(t.id)}
                      data-testid={`war-room-map-truck-${t.number}`}
                      title={`Truck ${t.number} · ${t.status?.replace("_", " ")} · GPS ${ageLabel}${trk.location_source === "simulated" ? " (simulated)" : ""}`}
                      className={`translate-x-[-50%] translate-y-[-50%] block cursor-pointer transition-transform duration-150 ${isSelected ? "scale-125" : "hover:scale-110"}`}
                    >
                      <img
                        src={truckPinDataUrl(tint, isSelected, trk.heading)}
                        alt={`Truck ${t.number}`}
                        className={`w-11 h-11 ${needsPS ? "pulse-red-strong rounded-full" : ""} ${stale ? "opacity-40" : ""}`}
                        draggable={false}
                      />
                      <div
                        className={`text-[10px] mono font-black px-1 py-0.5 -mt-1 whitespace-nowrap text-center ${stale ? "bg-black/60 text-white/60" : "bg-black/80 text-white"}`}
                        style={{ transform: "translateY(-2px)" }}
                        data-testid={`war-room-map-truck-label-${t.number}`}
                      >
                        {t.number}{stale ? ` · ${ageLabel}` : ""}
                      </div>
                    </button>
                  </OverlayView>
                );
              })}
            </GoogleMap>
            </MapErrorBoundary>
          )}

          {/* Selected-truck info card */}
          {selectedTruck && (
            <div
              className="absolute bottom-3 left-3 right-3 md:right-auto md:w-[300px] bg-surface border border-industrial shadow-xl p-3 rounded-none"
              data-testid="war-room-truck-info"
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-8 h-8 flex items-center justify-center"
                  style={{ background: STATUS_TINT[selectedTruck.status] || "#F97316" }}
                >
                  <TruckIcon className="w-4 h-4 text-black" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Truck</div>
                  <div className="heading font-black text-base leading-none">{selectedTruck.number}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedTruckId(null)}
                  className="text-muted-industrial hover:text-primary-orange mono text-xs"
                  data-testid="war-room-truck-info-close"
                >
                  ×
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Mix (MPa)</div>
                  <div className="font-bold" data-testid="war-room-truck-info-mpa">
                    {selectedMix
                      ? <>{selectedMix.strength_mpa} <span className="text-muted-industrial mono text-[10px]">MPa</span> <span className="text-primary-orange">· {selectedMix.code}</span></>
                      : selectedJob?.mix_code
                        ? <span className="text-primary-orange">{selectedJob.mix_code}</span>
                        : <span className="text-muted-industrial">—</span>}
                  </div>
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Volume</div>
                  <div className="font-bold" data-testid="war-room-truck-info-volume">
                    {selectedLoad != null ? <>{selectedLoad} <span className="text-muted-industrial mono text-[10px]">m³</span></> : "—"}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Destination</div>
                  <div className="text-xs truncate" data-testid="war-room-truck-info-destination">
                    {selectedJob ? (selectedJob.site_address || selectedJob.site_name || selectedJob.customer_name) : "Idle at plant"}
                  </div>
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">ETA</div>
                  <div className="font-bold flex items-center gap-1" data-testid="war-room-truck-info-eta">
                    <Clock className="w-3 h-3" />
                    {selectedJob?.truck_etas?.[selectedTruck.id]?.eta_to_site
                      || selectedJob?.truck_etas?.[selectedTruck.id]?.eta_back_to_plant
                      || <span className="text-muted-industrial">—</span>}
                  </div>
                </div>
                <div>
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Speed</div>
                  <div className="font-bold flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {selectedTrack?.speed_kph != null ? `${Math.round(selectedTrack.speed_kph)} km/h` : "—"}
                  </div>
                </div>
                {predictions[selectedTruck.id] && (
                  <div className="col-span-2 border-t border-industrial pt-1.5" data-testid="war-room-truck-info-predicted">
                    <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">AI Predicted Return · {predictions[selectedTruck.id].confidence} confidence</div>
                    <div className={`font-bold ${predictions[selectedTruck.id].confidence === "low" ? "text-muted-industrial" : "text-emerald-400"}`}>
                      ↺ {predictions[selectedTruck.id].predicted_return_local}
                      <span className="text-muted-industrial font-normal text-xs"> · in {predictions[selectedTruck.id].minutes_remaining} min · {predictions[selectedTruck.id].basis}</span>
                    </div>
                    <div className="mono text-[9px] text-muted-industrial mt-0.5" data-testid="war-room-predicted-reason">{predictions[selectedTruck.id].reason}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* v3.16 · Click-to-assign confirm dialog — pops the moment BOTH a job
          AND a truck are selected (either order works). Replaces the earlier
          floating bottom bar. */}
      <AlertDialog
        open={!!(selectedJobId && selectedTruckId)}
        onOpenChange={(o) => { if (!o) { setSelectedJobId(null); setSelectedTruckId(null); } }}
      >
        {selectedJobId && selectedTruckId && (() => {
          const j = jobById[selectedJobId];
          const t = truckById[selectedTruckId];
          if (!j || !t) return null;
          const needsPS = needsPreStartFlag(fleetHealth[t.id]);
          return (
            <AlertDialogContent
              data-testid="war-room-assign-dialog"
              className="rounded-none bg-surface border-industrial text-ink"
            >
              <AlertDialogHeader>
                <AlertDialogTitle className="heading uppercase tracking-wider text-primary-orange flex items-center gap-2">
                  <Zap className="w-4 h-4" /> Assign Truck
                </AlertDialogTitle>
                <AlertDialogDescription className="text-ink text-sm">
                  Assign <span className="mono font-bold text-primary-orange">{t.number}</span>
                  {" "}({t.capacity_m3} m³) to{" "}
                  <span className="mono font-bold text-primary-orange">{j.customer_name}</span>?
                  <div className="mt-2 mono text-[11px] text-muted-industrial">
                    {j.mix_code} · {j.quantity_m3} m³ · {j.delivery_address || "—"}
                  </div>
                  {needsPS && (
                    <div className="mt-2 mono text-[11px] text-red-400 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> {t.number} is MISSING today's pre-start.
                    </div>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  data-testid="war-room-assign-cancel"
                  className="rounded-none border-industrial uppercase tracking-widest text-xs"
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid="war-room-assign-confirm"
                  disabled={assigning}
                  onClick={(e) => { e.preventDefault(); assignSelected(); }}
                  className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase tracking-widest text-xs font-bold disabled:opacity-40"
                >
                  {assigning ? "Assigning…" : "Assign ▶"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          );
        })()}
      </AlertDialog>
    </div>
  );
}


// Clone multiple orders from any day onto one chosen day, in a single action.
function BulkCloneDialog({ open, onOpenChange, plantId, initialDate, onDone }) {
  const [fromDate, setFromDate] = useState(initialDate || localToday());
  const [toDate, setToDate] = useState(() => shiftLocalDate(initialDate || localToday(), 1));
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [cloning, setCloning] = useState(false);

  useEffect(() => { if (open) { setFromDate(initialDate || localToday()); setToDate(shiftLocalDate(initialDate || localToday(), 1)); } }, [open, initialDate]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const p = plantId ? `&plant_id=${plantId}` : "";
    api.get(`/dashboard?date=${fromDate}${p}`)
      .then((r) => { setJobs(r.data.jobs || []); setSelected(new Set((r.data.jobs || []).map((j) => j.id))); })
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [open, fromDate, plantId]);

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = jobs.length > 0 && selected.size === jobs.length;
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(jobs.map((j) => j.id)));

  const doClone = async () => {
    if (!selected.size) { toast.error("Tick at least one order"); return; }
    setCloning(true);
    try {
      const { data } = await api.post("/jobs/clone-bulk", { job_ids: [...selected], date: toDate });
      toast.success(`Cloned ${data.cloned} order${data.cloned === 1 ? "" : "s"} → ${formatLocalDate(toDate)}`);
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Clone failed"); }
    finally { setCloning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="bulk-clone-dialog">
        <DialogHeader><DialogTitle className="heading uppercase text-primary-orange flex items-center gap-2"><Copy className="w-4 h-4" /> Clone orders</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">From day</div>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="bulk-clone-from"
                className="bg-surface-2 border border-industrial px-2 py-1.5 mono text-xs text-ink focus:border-primary-orange outline-none" />
            </div>
            <div className="text-primary-orange pb-2">→</div>
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">Clone to day</div>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="bulk-clone-to"
                className="bg-surface-2 border border-industrial px-2 py-1.5 mono text-xs text-ink focus:border-primary-orange outline-none" />
            </div>
          </div>
          <div className="border border-industrial max-h-64 overflow-y-auto">
            <div className="flex items-center justify-between px-3 py-2 border-b border-industrial bg-surface-2 sticky top-0">
              <button type="button" onClick={toggleAll} data-testid="bulk-clone-select-all" className="mono text-[10px] uppercase tracking-widest text-primary-orange">
                {allOn ? "Clear all" : "Select all"}
              </button>
              <span className="mono text-[10px] text-muted-industrial">{selected.size}/{jobs.length} selected</span>
            </div>
            {loading && <div className="p-4 flex items-center gap-2 text-muted-industrial mono text-xs"><Loader2 className="w-4 h-4 animate-spin" /> loading orders…</div>}
            {!loading && jobs.length === 0 && <div className="p-4 text-center text-muted-industrial text-xs">No orders on {formatLocalDate(fromDate)}.</div>}
            {!loading && jobs.map((j) => (
              <label key={j.id} data-testid={`bulk-clone-job-${j.id}`} className="flex items-center gap-3 px-3 py-2 border-b border-industrial last:border-0 cursor-pointer hover:bg-surface-2">
                <input type="checkbox" checked={selected.has(j.id)} onChange={() => toggle(j.id)} className="accent-[#F57C00]" />
                <div className="min-w-0 flex-1">
                  <div className="mono text-xs font-bold text-ink truncate">{j.customer_name}</div>
                  <div className="mono text-[10px] text-muted-industrial truncate">{j.mix_code || j.mix_name} · {j.quantity_m3} m³{j.site_address ? ` · ${j.site_address}` : ""}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className="rounded-none border border-industrial px-4 py-2 mono text-xs uppercase text-muted-industrial hover:text-ink">Cancel</button>
          <button type="button" onClick={doClone} disabled={cloning || !selected.size} data-testid="bulk-clone-confirm"
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black px-4 py-2 mono text-xs uppercase font-black disabled:opacity-50">
            {cloning ? "Cloning…" : `Clone ${selected.size} → ${formatLocalDate(toDate)}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
